import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowRightIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import {
  DOCS_MEGA_COLUMNS,
  DOCS_OVERVIEW_HREF,
  TOOLS_OVERVIEW_HREF,
  type NavMegaMenuId,
  type NavToolMenuItem,
} from './navMenuConfig';
import { DocsMenuIcon } from './NavMenuIcons';
import { NavToolCard } from './ToolsMegaMenu';
import type { NavPrimaryLink } from './topNavigationHelpers';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';

type MobileNavDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  links: NavPrimaryLink[];
  linkLabels: Record<string, string>;
  visibleTools: NavToolMenuItem[];
  isAuthenticated: boolean;
  userDisplay: string;
  showLogin: boolean;
  onLogout: () => void;
};

function trackMobileNav(label: string, href: string, section: string) {
  trackEvent({
    name: 'navigation_click',
    buttonId: `navigation_mobile_${slugifyTrackingValue(href)}`,
    label,
    page: 'global',
    section,
    feature: 'navigation',
    eventCategory: 'navigation',
  });
}

export default function MobileNavDrawer({
  isOpen,
  onClose,
  links,
  linkLabels,
  visibleTools,
  isAuthenticated,
  userDisplay,
  showLogin,
  onLogout,
}: MobileNavDrawerProps) {
  const [expanded, setExpanded] = useState<NavMegaMenuId | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) setExpanded(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleNavigate = () => {
    onClose();
    setExpanded(null);
  };

  if (!isOpen || !mounted) return null;

  const drawer = (
    <>
      <button type="button" className="top-nav-mobile-backdrop" aria-label="Cerrar menú" onClick={onClose} />
      <nav
        className="top-nav-mobile top-nav-mobile--open"
        aria-label="Mobile navigation"
        role="dialog"
        aria-modal="true"
      >
        <header className="top-nav-mobile__header">
          <span className="top-nav-mobile__title">Menu</span>
          <button type="button" className="top-nav-mobile__close" onClick={onClose} aria-label="Cerrar">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </header>

        <div className="top-nav-mobile__scroll">
          {isAuthenticated && userDisplay && (
            <div className="top-nav-mobile__user">
              <UserCircleIcon className="top-nav-mobile__user-icon" aria-hidden />
              <span className="top-nav-mobile__user-email">{userDisplay}</span>
            </div>
          )}

          <ul className="top-nav-mobile__list">
            {links
              .filter(link => !link.showOnlyMobile)
              .map(link => {
                const label = linkLabels[link.cmsKey] ?? link.label;

                if (!link.megaMenu) {
                  return (
                    <li key={link.href} className="top-nav-mobile__item">
                      <Link
                        to={link.href}
                        className="top-nav-mobile__link"
                        onClick={() => {
                          trackMobileNav(label, link.href, 'top_navigation_mobile');
                          handleNavigate();
                        }}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                }

                const isExpanded = expanded === link.megaMenu;

                return (
                  <li key={link.href} className="top-nav-mobile__item top-nav-mobile__item--mega">
                    <button
                      type="button"
                      className={`top-nav-mobile__link top-nav-mobile__link--trigger ${isExpanded ? 'top-nav-mobile__link--active' : ''}`}
                      aria-expanded={isExpanded}
                      onClick={() => setExpanded(prev => (prev === link.megaMenu ? null : link.megaMenu!))}
                    >
                      {label}
                      <ChevronDownIcon
                        className={`top-nav-mobile__chevron ${isExpanded ? 'top-nav-mobile__chevron--open' : ''}`}
                        aria-hidden
                      />
                    </button>

                    {isExpanded && link.megaMenu === 'docs' && (
                      <div className="top-nav-mobile__panel">
                        <Link
                          to={DOCS_OVERVIEW_HREF}
                          className="top-nav-mobile__overview"
                          onClick={() => {
                            trackMobileNav('View all docs', DOCS_OVERVIEW_HREF, 'top_navigation_mobile_docs');
                            handleNavigate();
                          }}
                        >
                          View all docs
                          <ArrowRightIcon className="top-nav-mobile__overview-arrow" aria-hidden />
                        </Link>
                        {DOCS_MEGA_COLUMNS.map(column => (
                          <div key={column.title} className="top-nav-mobile__group">
                            <p className="top-nav-mobile__group-title">{column.title}</p>
                            <ul className="top-nav-mobile__sublist">
                              {column.items.map(item => (
                                <li key={item.href}>
                                  <Link
                                    to={item.href}
                                    className="top-nav-mobile__subitem"
                                    onClick={() => {
                                      trackMobileNav(item.label, item.href, 'top_navigation_mobile_docs');
                                      handleNavigate();
                                    }}
                                  >
                                    <span className="top-nav-mobile__subitem-icon" aria-hidden>
                                      <DocsMenuIcon icon={item.icon} />
                                    </span>
                                    <span className="top-nav-mobile__subitem-text">
                                      <span className="top-nav-mobile__subitem-label">{item.label}</span>
                                      <span className="top-nav-mobile__subitem-desc">{item.subtitle}</span>
                                    </span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {isExpanded && link.megaMenu === 'tools' && (
                      <div className="top-nav-mobile__panel top-nav-mobile__panel--tools">
                        <div className="top-nav-mobile__panel-intro">
                          <p className="nav-mega-section-title">TOOLS</p>
                          <p className="top-nav-mobile__panel-subtitle">Explore the DISGENET ecosystem</p>
                        </div>
                        <Link
                          to={TOOLS_OVERVIEW_HREF}
                          className="top-nav-mobile__overview"
                          onClick={() => {
                            trackMobileNav(
                              'View all tools',
                              TOOLS_OVERVIEW_HREF,
                              'top_navigation_mobile_tools',
                            );
                            handleNavigate();
                          }}
                        >
                          View all tools
                          <ArrowRightIcon className="top-nav-mobile__overview-arrow" aria-hidden />
                        </Link>
                        <div className="top-nav-mobile__tools-stack">
                          {visibleTools.map(tool => (
                            <NavToolCard
                              key={tool.id}
                              tool={tool}
                              onNavigate={handleNavigate}
                              trackingSection="top_navigation_mobile_tools"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
          </ul>
        </div>

        <footer className="top-nav-mobile__footer">
          {isAuthenticated ? (
            <>
              <Link
                to="/Profile-area"
                className="top-nav-mobile__footer-link"
                onClick={() => {
                  trackMobileNav('Profile Data', '/Profile-area', 'top_navigation_mobile');
                  handleNavigate();
                }}
              >
                Profile Data
              </Link>
              <button type="button" className="top-nav-mobile__footer-logout" onClick={onLogout}>
                Logout
              </button>
            </>
          ) : (
            showLogin && (
              <Link
                to="/login"
                className="top-nav-mobile__footer-login"
                onClick={() => {
                  trackMobileNav('Login', '/login', 'top_navigation_mobile');
                  handleNavigate();
                }}
              >
                Log in
              </Link>
            )
          )}
        </footer>
      </nav>
    </>
  );

  return createPortal(drawer, document.body);
}
